/**
 * شاشة «المستخدمون والصلاحيات» (design/Access.dc.html).
 *
 * الجديد من قرار المؤسس (docs/09): **قسم «طلبات انضمام (N)» فوق الجدول**
 * بعدّاد أحمر. الاعتماد بيفتح نموذج المدير بيحدّد فيه القسم والدور —
 * ⛔ مش بياخدهم من كلام الموظف. موظف كتب إنه «مدير» ما ياخدش دور مدير.
 * والرفض بسبب مكتوب إجباري، والسجل بيفضل محفوظ.
 *
 * ⚠️ **المراقب ما بيكتبش أبدًا**: الأزرار مقفولة له هنا، والباك إند بيرفض
 *    كمان. القفل في الواجهة راحة مش أمان — الأمان في الاستعلام نفسه.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Shell } from "@/components/Shell";
import { Icon } from "@/components/Icon";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoPermissionState,
  NoResultsState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { OfflineError, errorMessageAr } from "@/api/client";
import {
  DEPARTMENTS,
  ROLES,
  approveJoinRequest,
  auditLabel,
  createUser,
  departmentLabel,
  SUBJECTS,
  subjectLabel,
  inviteUser,
  issueTempPassword,
  listAuditLog,
  listJoinRequests,
  listUsers,
  rejectJoinRequest,
  roleLabel,
  statusLabel,
  updateUserAccess,
  archiveUser,
  type AdminRole,
  type AdminUser,
  type AdminUserCounts,
  type AuditEntry,
} from "@/api/admin";
import { Ltr, fmtNum, fmtRelative } from "@/lib/format";
import { HOME_PATH, useRouter } from "@/lib/router";
import { useSession } from "@/lib/session";

/** التصميم بيعرض «عرض 1–7 من 14» */
const USERS_PER_PAGE = 7;
const JOIN_PER_PAGE = 10;
const AUDIT_LIMIT = 20;
/** أقل طول لسبب الرفض — نفس حدّ السيرفر عشان ما يرفضش الطلب بعد ما يتبعت */
const MIN_REASON = 3;

type ListState<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

type UsersData = { rows: AdminUser[]; total: number; totalPages: number; counts: AdminUserCounts | null };
type JoinData = { rows: AdminUser[]; total: number };

type Modal =
  | { kind: "none" }
  | { kind: "approve"; user: AdminUser }
  | { kind: "reject"; user: AdminUser }
  | { kind: "archive"; user: AdminUser }
  | { kind: "edit"; user: AdminUser }
  | { kind: "create" };

type Flash = { tone: "ok" | "bad"; node: ReactNode };

export function Access() {
  const { me } = useSession();
  const { navigate } = useRouter();

  const perms = useMemo(() => new Set(me?.permissions ?? []), [me]);
  const isObserver = me?.user.role === "observer";
  const canRead = perms.has("user.read");
  // ⛔ المراقب مستثنى صراحةً حتى لو المفتاح موجود — «للاطلاع بس»
  const canApprove = perms.has("user.approve") && !isObserver;
  const canWrite = perms.has("user.write") && !isObserver;
  const canCreate = perms.has("user.create") && !isObserver;
  const canAudit = perms.has("audit.read");

  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [usersNonce, setUsersNonce] = useState(0);
  const [joinNonce, setJoinNonce] = useState(0);

  const [users, setUsers] = useState<ListState<UsersData>>({ kind: "loading" });
  const [joins, setJoins] = useState<ListState<JoinData>>({ kind: "loading" });

  const [modal, setModal] = useState<Modal>({ kind: "none" });
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [tempBusyId, setTempBusyId] = useState<string | null>(null);
  const [roleBusyId, setRoleBusyId] = useState<string | null>(null);
  const [deptBusyId, setDeptBusyId] = useState<string | null>(null);
  const [extraBusyId, setExtraBusyId] = useState<string | null>(null);
  const [subjBusyId, setSubjBusyId] = useState<string | null>(null);
  // الكلمة المعروضة مرة واحدة — لوحة بتتقفل بالإيد بس
  const [tempPass, setTempPass] = useState<{
    name: string;
    password: string;
    minutes: number;
    warning: string;
  } | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);

  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState<ListState<AuditEntry[]>>({ kind: "loading" });

  /* ───────────── البحث بتمهُّل ───────────── */

  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(id);
  }, [query]);

  /* ───────────── جلب المستخدمين ───────────── */

  useEffect(() => {
    if (!canRead) return;
    const ctrl = new AbortController();
    let alive = true;
    setUsers({ kind: "loading" });
    listUsers({
      ...(search.length >= 2 ? { q: search } : {}),
      page,
      perPage: USERS_PER_PAGE,
      withCounts: true,
      signal: ctrl.signal,
    })
      .then((res) => {
        if (!alive) return;
        setUsers({
          kind: "ready",
          data: {
            rows: res.rows,
            total: res.meta?.total ?? res.rows.length,
            totalPages: Math.max(1, res.meta?.total_pages ?? 1),
            counts: res.counts,
          },
        });
      })
      .catch((err: unknown) => {
        if (!alive || (err as Error)?.name === "AbortError") return;
        setUsers(
          err instanceof OfflineError
            ? { kind: "offline" }
            : { kind: "error", messageAr: errorMessageAr(err) },
        );
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [canRead, search, page, usersNonce]);

  /* ───────────── جلب طلبات الانضمام ───────────── */

  useEffect(() => {
    if (!canApprove) return;
    const ctrl = new AbortController();
    let alive = true;
    setJoins({ kind: "loading" });
    listJoinRequests({ page: 1, perPage: JOIN_PER_PAGE, signal: ctrl.signal })
      .then((res) => {
        if (!alive) return;
        setJoins({
          kind: "ready",
          data: { rows: res.rows, total: res.meta?.total ?? res.rows.length },
        });
      })
      .catch((err: unknown) => {
        if (!alive || (err as Error)?.name === "AbortError") return;
        setJoins(
          err instanceof OfflineError
            ? { kind: "offline" }
            : { kind: "error", messageAr: errorMessageAr(err) },
        );
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [canApprove, joinNonce]);

  /* ───────────── سجل الدخول والتغييرات ───────────── */

  const loadAudit = useCallback((signal?: AbortSignal) => {
    setAudit({ kind: "loading" });
    listAuditLog({ limit: AUDIT_LIMIT, ...(signal ? { signal } : {}) })
      .then((rows) => setAudit({ kind: "ready", data: rows }))
      .catch((err: unknown) => {
        if ((err as Error)?.name === "AbortError") return;
        setAudit(
          err instanceof OfflineError
            ? { kind: "offline" }
            : { kind: "error", messageAr: errorMessageAr(err) },
        );
      });
  }, []);

  useEffect(() => {
    if (!auditOpen) return;
    const ctrl = new AbortController();
    loadAudit(ctrl.signal);
    return () => ctrl.abort();
  }, [auditOpen, loadAudit]);

  /* ───────────── الأفعال ───────────── */

  function refreshAll() {
    setUsersNonce((n) => n + 1);
    setJoinNonce((n) => n + 1);
  }

  /**
   * «كلمة سر مؤقتة» — بتتعرض **مرة واحدة** في لوحة لازم تتقفل بالإيد.
   *
   * ⛔ عمدًا مش في `flash`: الفلاش بيختفي مع أول تحديث للجدول، والكلمة
   *    لو ضاعت **مستحيل نرجّعها** — لازم إصدار جديد. اللوحة بتفضل مفتوحة
   *    لحد ما المدير ينسخها ويقفلها بنفسه.
   */
  async function onTempPassword(user: AdminUser) {
    if (tempBusyId) return;
    setTempBusyId(user.id);
    setFlash(null);
    try {
      const out = await issueTempPassword(user.id);
      setTempPass({
        name: user.full_name_ar,
        password: out.password,
        minutes: out.expiresInMinutes,
        warning: out.warningAr,
      });
      setUsersNonce((n) => n + 1);
    } catch (err) {
      setFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setTempBusyId(null);
    }
  }

  /** تغيير القسم من الجدول.
   *
   * 🔴 **كان مفيش أي طريقة تحدّد قسم لحساب موجود (٢٠٢٦-٠٩-٢١).** العمود
   *    كان نصًّا معروضًا وبس، والخادم بيقبل الحقل من زمان. النتيجة: حساب
   *    اتعمل «بلا قسم» بيفضل كده للأبد، وصاحبه بيفتح السيستم ويلاقيه
   *    **فاضي** — لأن كل فلاتر النطاق بتقف على القسم.
   */
  async function onDeptChange(user: AdminUser, department: string) {
    if (deptBusyId || department === (user.department ?? "")) return;
    setDeptBusyId(user.id);
    setFlash(null);
    try {
      await updateUserAccess(user.id, { department });
      setFlash({
        tone: "ok",
        node: `${user.full_name_ar}: القسم بقى ${departmentLabel(department)}`,
      });
      setUsersNonce((n) => n + 1);
    } catch (err) {
      setFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setDeptBusyId(null);
    }
  }

  /** تخصصات المقيّمة — بتتظبط بالإضافة والشيل، ومن غيرها الخريطة بتفلتر
   *  على خانة فاضية فالمقيّمة ما بتظهرش لدعم الطلاب خالص. */
  async function onSubjectToggle(user: AdminUser, subject: string) {
    if (subjBusyId) return;
    const current = user.subjects ?? [];
    const next = current.includes(subject)
      ? current.filter((s) => s !== subject)
      : [...current, subject];
    setSubjBusyId(user.id);
    setFlash(null);
    try {
      await updateUserAccess(user.id, { subjects: next });
      setFlash({
        tone: "ok",
        node: `${user.full_name_ar}: التخصصات بقت ${
          next.length ? next.map(subjectLabel).join(" · ") : "بلا تخصص"
        }`,
      });
      setUsersNonce((n) => n + 1);
    } catch (err) {
      setFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setSubjBusyId(null);
    }
  }

  /** قسم إضافي — الموظف بيشوف طوابير ومهام القسمين. طلب المؤسس (٦ أكتوبر):
   *  موظفة في قسم وعايزينها تقيّم كمان. السيرفر كان بيقبل الحقل من زمان
   *  ومفيش زرار كان بيوصله. */
  async function onExtraDeptToggle(user: AdminUser, dept: string) {
    if (extraBusyId) return;
    const current = user.extra_departments ?? [];
    const next = current.includes(dept)
      ? current.filter((d) => d !== dept)
      : [...current, dept];
    setExtraBusyId(user.id);
    setFlash(null);
    try {
      await updateUserAccess(user.id, { extra_departments: next });
      setFlash({
        tone: "ok",
        node: `${user.full_name_ar}: ${
          next.length ? `كمان في ${next.map(departmentLabel).join(" · ")}` : "في قسمه الأساسي بس"
        }`,
      });
      setUsersNonce((n) => n + 1);
    } catch (err) {
      setFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setExtraBusyId(null);
    }
  }

  /** تغيير الدور من الجدول — السيستم بيرفض رتبة زيّك أو أعلى */
  async function onRoleChange(user: AdminUser, role: AdminRole) {
    if (roleBusyId || role === user.role) return;
    setRoleBusyId(user.id);
    setFlash(null);
    try {
      await updateUserAccess(user.id, { role });
      setFlash({
        tone: "ok",
        node: `${user.full_name_ar}: الدور بقى ${roleLabel(role)}`,
      });
      setUsersNonce((n) => n + 1);
    } catch (err) {
      setFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setRoleBusyId(null);
    }
  }

  async function onInvite(user: AdminUser) {
    if (invitingId) return;
    setInvitingId(user.id);
    setFlash(null);
    try {
      const message = await inviteUser(user.id);
      // النص جاي من السيستم وبيتعرض زي ما هو
      setFlash({ tone: "ok", node: `${user.full_name_ar}: ${message}` });
      setUsersNonce((n) => n + 1);
    } catch (err) {
      setFlash({ tone: "bad", node: errorMessageAr(err) });
    } finally {
      setInvitingId(null);
    }
  }

  const usersData = users.kind === "ready" ? users.data : null;
  const counts = usersData?.counts ?? null;
  const joinsData = joins.kind === "ready" ? joins.data : null;
  const pendingJoins = joinsData?.total ?? 0;

  const total = usersData?.total ?? 0;
  const rows = usersData?.rows ?? [];
  const firstIndex = total === 0 ? 0 : (page - 1) * USERS_PER_PAGE + 1;
  const lastIndex = (page - 1) * USERS_PER_PAGE + rows.length;
  const totalPages = usersData?.totalPages ?? 1;

  const crumb = counts ? (
    <>
      <span className="num">{fmtNum(counts.active + counts.pending_activation)}</span> حساب ·{" "}
      <span className="num">{fmtNum(counts.telegram_unlinked)}</span> مش مربوطين بتليجرام
    </>
  ) : (
    "الحسابات والأدوار وطلبات الانضمام"
  );

  if (!canRead) {
    return (
      <Shell title="المستخدمون والصلاحيات">
        <NoPermissionState
          messageAr="شاشة المستخدمين مش من صلاحياتك"
          onBack={() => navigate(HOME_PATH)}
          fill
        />
      </Shell>
    );
  }

  return (
    <Shell
      title="المستخدمون والصلاحيات"
      crumb={crumb}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "ابحث باسم المستخدم أو البريد…",
      }}
    >
      {isObserver ? (
        <div className="card flat" style={{ padding: "12px 16px", display: "flex", gap: 10 }}>
          <Icon name="lock" size={16} stroke="#98A2B3" />
          <span className="t-desc">
            إنت داخل بصفة مراقب — الاطلاع بس. أزرار التعديل والاعتماد مقفولة.
          </span>
        </div>
      ) : null}

      {flash ? <FlashLine flash={flash} /> : null}

      {/* ⚠️ فوق كل حاجة وبتتقفل بالإيد — الكلمة ما بترجعش تاني أبدًا */}
      {tempPass ? (
        <div
          className="card"
          role="alert"
          style={{
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            border: "2px solid var(--gold, #c9a44c)",
          }}
        >
          <span className="sec-title">كلمة سر مؤقتة — {tempPass.name}</span>
          <code
            dir="ltr"
            style={{
              fontSize: 20,
              letterSpacing: 1,
              padding: "10px 14px",
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: "var(--r-control)",
              userSelect: "all",
              textAlign: "center",
            }}
          >
            {tempPass.password}
          </code>
          <span className="t-desc">
            {tempPass.warning ||
              "اكتبها دلوقتي — مش هتظهر تاني، والدخول بيها بيطلب تغييرها فورًا"}
          </span>
          {tempPass.minutes > 0 ? (
            <span className="t-meta">
              بتنتهي خلال <Ltr>{fmtNum(tempPass.minutes)}</Ltr> دقيقة — ابعتها وهو قدام الشاشة.
            </span>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                void navigator.clipboard?.writeText(tempPass.password);
              }}
            >
              نسخ
            </button>
            <button type="button" className="btn sm ghost" onClick={() => setTempPass(null)}>
              نسختها — اقفل
            </button>
          </div>
        </div>
      ) : null}

      {/* ═══════════ (1) صف الإحصاء ═══════════ */}
      <div className="stats c3">
        <div className="stat" style={{ cursor: "default" }}>
          <div className="v">
            <span className="num">{fmtNum(counts?.active ?? 0)}</span>
          </div>
          <div className="l">حسابات نشطة</div>
        </div>
        <div className="stat" style={{ cursor: "default" }}>
          <div className="v">
            <span className="num">{fmtNum(counts?.pending_activation ?? 0)}</span>
          </div>
          <div className="l">مستنيين تفعيل</div>
        </div>
        <div className="stat" style={{ cursor: "default" }}>
          <div className="v">
            <span className="num">{fmtNum(counts?.telegram_unlinked ?? 0)}</span>
          </div>
          <div className="l">مش مربوطين بتليجرام</div>
        </div>
      </div>

      {/* ═══════════ (2) طلبات انضمام — فوق الجدول بعدّاد أحمر ═══════════ */}
      {canApprove ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="sec-h">
            <span className="sec-title">طلبات انضمام</span>
            <span className={`chip ${pendingJoins > 0 ? "late" : "ghost"}`}>
              <span className="bullet" />
              <span className="num">{fmtNum(pendingJoins)}</span>
            </span>
            <span className="sec-note">
              القسم والدور بتحدّدهم إنت — اللي كتبه الموظف عن نفسه استرشادي بس
            </span>
          </div>

          {joins.kind === "loading" ? <LoadingState rows={3} /> : null}
          {joins.kind === "offline" ? (
            <OfflineState onRetry={() => setJoinNonce((n) => n + 1)} />
          ) : null}
          {joins.kind === "error" ? (
            <ErrorState
              messageAr={joins.messageAr}
              onRetry={() => setJoinNonce((n) => n + 1)}
            />
          ) : null}

          {joinsData && joinsData.rows.length === 0 ? (
            <EmptyState
              title="مفيش طلبات انضمام مستنية"
              hint="أي حد يسجّل ويأكّد بريده هيظهر هنا"
            />
          ) : null}

          {joinsData && joinsData.rows.length > 0 ? (
            <div className="card" style={{ padding: 0, overflowX: "auto" }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 220 }}>الاسم</th>
                    <th style={{ width: 230 }}>البريد</th>
                    <th style={{ width: 200 }}>قال إنه في</th>
                    <th style={{ width: 150 }}>وقت التسجيل</th>
                    <th>إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {joinsData.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar name={row.full_name_ar} />
                          <span className="t-body clip">{row.full_name_ar}</span>
                        </div>
                      </td>
                      <td>
                        {row.email ? (
                          <span className="t-desc">
                            <Ltr>{row.email}</Ltr>
                          </span>
                        ) : (
                          <span className="t-meta">بلا بريد</span>
                        )}
                      </td>
                      <td>
                        <span className="t-desc">
                          {row.claimed_department
                            ? departmentLabel(row.claimed_department)
                            : "ما كتبش قسم"}
                        </span>
                      </td>
                      <td>
                        <span className="t-desc">
                          {row.created_at ? fmtRelative(row.created_at) : "—"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <button
                            type="button"
                            className="btn sm ghost"
                            onClick={() => setModal({ kind: "approve", user: row })}
                          >
                            اعتماد
                          </button>
                          <button
                            type="button"
                            className="btn sm danger"
                            onClick={() => setModal({ kind: "reject", user: row })}
                          >
                            رفض
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ═══════════ (3) جدول المستخدمين ═══════════ */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="sec-h" style={{ flexWrap: "wrap" }}>
          <span className="sec-title">المستخدمون</span>
          <button
            type="button"
            className="btn primary"
            disabled={!canCreate}
            title={canCreate ? undefined : "مش من صلاحياتك"}
            onClick={() => setModal({ kind: "create" })}
          >
            <Icon name="hiring" size={16} stroke="#FFFFFF" />
            إضافة مستخدم
          </button>
          <span className="sec-note">
            المعلم مالوش حساب هنا — بيستقبل إشعارات تليجرام بس
          </span>
          {canAudit ? (
            <button
              type="button"
              className="link"
              style={{ marginRight: "auto" }}
              aria-expanded={auditOpen}
              onClick={() => setAuditOpen((v) => !v)}
            >
              {auditOpen ? "إخفاء سجل الدخول والتغييرات" : "سجل الدخول والتغييرات"}
            </button>
          ) : null}
        </div>

        {users.kind === "loading" ? <LoadingState rows={5} /> : null}
        {users.kind === "offline" ? (
          <OfflineState onRetry={() => setUsersNonce((n) => n + 1)} />
        ) : null}
        {users.kind === "error" ? (
          <ErrorState messageAr={users.messageAr} onRetry={() => setUsersNonce((n) => n + 1)} />
        ) : null}

        {usersData && rows.length === 0 && search.length >= 2 ? (
          <NoResultsState query={search} onClear={() => setQuery("")} />
        ) : null}

        {usersData && rows.length === 0 && search.length < 2 ? (
          <EmptyState title="مفيش حسابات هنا" hint="ابدأ بإضافة أول مستخدم" />
        ) : null}

        {rows.length > 0 ? (
          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            {/* 🔑 الجدول للعرض بس — زي التصميم المعتمد (design/Access.dc.html).
                القوائم والأزرار كانت جوّه الصفوف فبقت «كل حاجة فوق بعضها»؛
                التعديل كله في نافذة واحدة من زرار «تعديل». */}
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 290 }}>الاسم</th>
                  <th style={{ width: 210 }}>القسم</th>
                  <th style={{ width: 130 }}>الدور</th>
                  <th style={{ width: 150 }}>آخر دخول</th>
                  <th style={{ width: 120 }}>تليجرام</th>
                  <th>إجراء</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const badge = statusLabel(row.status);
                  const linked = row.telegram?.linked === true;
                  const extras = (row.extra_departments ?? []).filter((d) => d !== row.department);
                  return (
                    <tr key={row.id} className="row-link">
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                          <Avatar name={row.full_name_ar} />
                          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                            <span className="t-body clip">{row.full_name_ar}</span>
                            {/* البريد تحت الاسم — عشان حسابين بنفس الاسم يتفرّقوا */}
                            {row.email ? (
                              <span className="t-meta clip">
                                <Ltr>{row.email}</Ltr>
                              </span>
                            ) : null}
                          </div>
                          {badge ? (
                            <span className="chip late" style={{ flexShrink: 0 }}>
                              <span className="bullet" />
                              {badge}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <span className="t-desc">
                            {row.department ? departmentLabel(row.department) : (
                              <span className="missing">بلا قسم</span>
                            )}
                          </span>
                          {extras.length > 0 ? (
                            <span className="t-meta">+ {extras.map(departmentLabel).join(" · ")}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className="t-desc">{roleLabel(row.role)}</span>
                      </td>
                      <td>
                        {row.last_login_at ? (
                          <span className="t-desc">{fmtRelative(row.last_login_at)}</span>
                        ) : (
                          <span className="t-meta">لسه ما دخلش</span>
                        )}
                      </td>
                      <td>
                        <span className={`chip ${linked ? "okc" : "soon"}`}>
                          <span className="bullet" />
                          {linked ? "متصل" : "مش متصل"}
                        </span>
                      </td>
                      <td>
                        {!canWrite ? (
                          <span className="t-meta">الاطلاع بس</span>
                        ) : (
                          <button
                            type="button"
                            className="btn sm ghost"
                            onClick={() => setModal({ kind: "edit", user: row })}
                          >
                            تعديل
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {rows.length > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="t-meta">
              عرض <span className="num">{fmtNum(firstIndex)}</span>–
              <span className="num">{fmtNum(lastIndex)}</span> من{" "}
              <span className="num">{fmtNum(total)}</span>
            </span>
            <button
              type="button"
              className="btn sm ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              السابق
            </button>
            <button
              type="button"
              className="btn sm ghost"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              التالي
            </button>
          </div>
        ) : null}
      </div>

      {/* ═══════════ (4) سجل الدخول والتغييرات ═══════════ */}
      {canAudit && auditOpen ? (
        <div
          className="card"
          style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}
        >
          <div className="sec-h">
            <span className="sec-title">سجل الدخول والتغييرات</span>
            <span className="sec-note">
              آخر <span className="num">{fmtNum(AUDIT_LIMIT)}</span> حركة · السجل ما بيتعدّلش ولا
              بيتمسح
            </span>
          </div>
          {audit.kind === "loading" ? <LoadingState rows={4} /> : null}
          {audit.kind === "offline" ? <OfflineState onRetry={() => loadAudit()} /> : null}
          {audit.kind === "error" ? (
            <ErrorState messageAr={audit.messageAr} onRetry={() => loadAudit()} />
          ) : null}
          {audit.kind === "ready" && audit.data.length === 0 ? (
            <EmptyState title="السجل لسه فاضي" hint="أول حركة هتتسجّل هنا" />
          ) : null}
          {audit.kind === "ready" && audit.data.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {audit.data.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    borderTop: "1px solid var(--line)",
                    paddingTop: 8,
                  }}
                >
                  <span className="t-body">{auditLabel(entry.action)}</span>
                  {entry.reason_ar ? (
                    <span className="t-desc clip">— {entry.reason_ar}</span>
                  ) : null}
                  <span className="t-meta" style={{ marginRight: "auto" }}>
                    {entry.at ? fmtRelative(entry.at) : "—"}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ═══════════ (5) الأدوار الأربعة ═══════════ */}
      <RoleMatrix />

      {/* ═══════════ النماذج ═══════════ */}
      {modal.kind === "approve" ? (
        <ApproveDialog
          user={modal.user}
          onClose={() => setModal({ kind: "none" })}
          onDone={(name, dept, role) => {
            setModal({ kind: "none" });
            setFlash({
              tone: "ok",
              node: `اتعتمد حساب ${name} — ${departmentLabel(dept)} · ${roleLabel(role)}`,
            });
            refreshAll();
          }}
        />
      ) : null}

      {modal.kind === "reject" ? (
        <RejectDialog
          user={modal.user}
          onClose={() => setModal({ kind: "none" })}
          onDone={(name) => {
            setModal({ kind: "none" });
            setFlash({ tone: "ok", node: `اترفض طلب ${name} — والسجل اتحفظ` });
            refreshAll();
          }}
        />
      ) : null}

      {modal.kind === "edit" ? (
        <EditUserDialog
          user={rows.find((r) => r.id === modal.user.id) ?? modal.user}
          isSelf={modal.user.id === me?.user.id}
          canApprove={canApprove}
          busy={{
            dept: deptBusyId !== null,
            extra: extraBusyId !== null,
            subjects: subjBusyId !== null,
            role: roleBusyId !== null,
            invite: invitingId !== null,
            temp: tempBusyId !== null,
          }}
          onDept={(d) => void onDeptChange(modal.user, d)}
          onExtra={(d) => void onExtraDeptToggle(modal.user, d)}
          onSubject={(sub) => void onSubjectToggle(modal.user, sub)}
          onRole={(r) => void onRoleChange(modal.user, r)}
          onInvite={() => void onInvite(modal.user)}
          onTempPassword={() => {
            setModal({ kind: "none" });
            void onTempPassword(modal.user);
          }}
          onArchive={() => setModal({ kind: "archive", user: modal.user })}
          onClose={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "archive" ? (
        <ArchiveDialog
          user={modal.user}
          onClose={() => setModal({ kind: "none" })}
          onDone={(name) => {
            setModal({ kind: "none" });
            setFlash({ tone: "ok", node: `اتمسح حساب ${name} — والسبب اتحفظ في السجل` });
            refreshAll();
          }}
        />
      ) : null}

      {modal.kind === "create" ? (
        <CreateUserDialog
          onClose={() => setModal({ kind: "none" })}
          onDone={(name) => {
            setModal({ kind: "none" });
            setFlash({
              tone: "ok",
              node: `اتعمل حساب ${name} — مستني تفعيل. ابعتله دعوة التفعيل من الجدول`,
            });
            setUsersNonce((n) => n + 1);
          }}
        />
      ) : null}
    </Shell>
  );
}

/* ══════════════════════════════════════════════════════════════════
   نماذج القرار
   ══════════════════════════════════════════════════════════════════ */

function ApproveDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: (name: string, department: string, role: AdminRole) => void;
}) {
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState<AdminRole | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    if (!department) {
      setError("اختار القسم اللي الحساب هيشتغل فيه");
      return;
    }
    if (!role) {
      setError("اختار دور الحساب");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await approveJoinRequest(user.id, { department, role });
      onDone(user.full_name_ar, department, role);
    } catch (err) {
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="اعتماد طلب انضمام" onClose={onClose} busy={busy}>
      <ApplicantSummary user={user} />

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          background: "var(--warn-bg)",
          color: "var(--warn)",
          borderRadius: "var(--r-control)",
          padding: "10px 12px",
          fontSize: "var(--fs-sm)",
          lineHeight: 1.6,
        }}
      >
        <Icon name="alert" size={16} stroke="#B54708" />
        <span>
          القسم والدور بتحدّدهم إنت. اللي الموظف كتبه عن نفسه معروض للاسترشاد وبس وما بياخدش
          أي صلاحية.
        </span>
      </div>

      <Field label="القسم">
        <select
          value={department}
          disabled={busy}
          onChange={(e) => setDepartment(e.target.value)}
          style={selectStyle}
        >
          <option value="">اختار القسم</option>
          {DEPARTMENTS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="الدور">
        <select
          value={role}
          disabled={busy}
          onChange={(e) => setRole(e.target.value as AdminRole | "")}
          style={selectStyle}
        >
          <option value="">اختار الدور</option>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </Field>

      {error ? <DialogError message={error} /> : null}

      <DialogActions>
        <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
          إلغاء
        </button>
        <button type="button" className="btn primary" disabled={busy} onClick={() => void submit()}>
          {busy ? "بيتعتمد…" : "اعتماد الحساب"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

function RejectDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    if (reason.trim().length < MIN_REASON) {
      setError("اكتب سبب الرفض — بيتحفظ في السجل وبيبان لمين يراجع بعدين");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await rejectJoinRequest(user.id, reason.trim());
      onDone(user.full_name_ar);
    } catch (err) {
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="رفض طلب انضمام" onClose={onClose} busy={busy}>
      <ApplicantSummary user={user} />

      <Field label="سبب الرفض (إجباري)">
        <textarea
          value={reason}
          disabled={busy}
          onChange={(e) => setReason(e.target.value)}
          placeholder="مثال: الاسم مش معروف في الأكاديمية"
          style={{ ...selectStyle, height: 92, padding: 12, resize: "vertical", lineHeight: 1.6 }}
        />
      </Field>

      <div className="t-meta" style={{ lineHeight: 1.7 }}>
        الرفض ما بيمسحش السجل — الحساب بيتقفل وبيفضل محفوظ.
      </div>

      {error ? <DialogError message={error} /> : null}

      <DialogActions>
        <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
          إلغاء
        </button>
        <button type="button" className="btn danger" disabled={busy} onClick={() => void submit()}>
          {busy ? "بيترفض…" : "رفض الطلب"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * نافذة تعديل حساب — كل اللي كان متكدس في صف الجدول: القسم ← كمان في ←
 * التخصصات ← الدور، وتحتهم الإجراءات، والمسح لوحده في الآخر بالأحمر.
 * كل تغيير بيتحفظ فورًا (نفس سلوك الصف القديم) والنتيجة بتبان في شريط الرسالة.
 */
function EditUserDialog({
  user,
  isSelf,
  canApprove,
  busy,
  onDept,
  onExtra,
  onSubject,
  onRole,
  onInvite,
  onTempPassword,
  onArchive,
  onClose,
}: {
  user: AdminUser;
  isSelf: boolean;
  canApprove: boolean;
  busy: { dept: boolean; extra: boolean; subjects: boolean; role: boolean; invite: boolean; temp: boolean };
  onDept: (department: string) => void;
  onExtra: (department: string) => void;
  onSubject: (subject: string) => void;
  onRole: (role: AdminRole) => void;
  onInvite: () => void;
  onTempPassword: () => void;
  onArchive: () => void;
  onClose: () => void;
}) {
  const locked = user.is_owner === true;
  const needsInvite = user.status === "pending_activation" || user.status === "pending_email";
  const evaluates =
    user.department === "evaluation" || (user.extra_departments ?? []).includes("evaluation");
  const anyBusy = busy.dept || busy.extra || busy.subjects || busy.role || busy.invite || busy.temp;

  return (
    <Dialog title={`تعديل حساب — ${user.full_name_ar}`} onClose={onClose} busy={anyBusy}>
      <ApplicantSummary user={user} />

      {locked ? (
        <div className="t-desc">ده حساب المالك — قسمه ودوره ثابتين.</div>
      ) : (
        <>
          <Field label="القسم الأساسي">
            <select
              style={selectStyle}
              value={user.department ?? ""}
              disabled={busy.dept}
              onChange={(e) => onDept(e.target.value)}
            >
              <option value="" disabled>
                بلا قسم
              </option>
              {DEPARTMENTS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </Field>

          {user.department ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="t-meta">كمان في (بيشوف مهام وطوابير القسم ده كمان)</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {DEPARTMENTS.filter((d) => d.value !== user.department).map((d) => {
                  const on = (user.extra_departments ?? []).includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      className={`btn sm ${on ? "accent" : "ghost"}`}
                      disabled={busy.extra}
                      aria-pressed={on}
                      onClick={() => onExtra(d.value)}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {evaluates ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="t-meta">تخصصات التقييم (الخريطة بتفلتر عليها)</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {SUBJECTS.map((sub) => {
                  const on = (user.subjects ?? []).includes(sub.value);
                  return (
                    <button
                      key={sub.value}
                      type="button"
                      className={`btn sm ${on ? "accent" : "ghost"}`}
                      disabled={busy.subjects}
                      aria-pressed={on}
                      onClick={() => onSubject(sub.value)}
                    >
                      {sub.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <Field label="الدور">
            <select
              style={selectStyle}
              value={user.role ?? ""}
              disabled={busy.role}
              onChange={(e) => onRole(e.target.value as AdminRole)}
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}

      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        <span className="t-meta">إجراءات</span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {needsInvite ? (
            <button type="button" className="btn sm ghost" disabled={busy.invite} onClick={onInvite}>
              {busy.invite ? "بيتبعت…" : "إرسال دعوة تفعيل"}
            </button>
          ) : null}
          <button
            type="button"
            className="btn sm ghost"
            disabled={busy.temp}
            onClick={onTempPassword}
            title="بتتعرض مرة واحدة — والحساب المعلّق بيتفعّل معاها"
          >
            {busy.temp ? "بيتعمل…" : "كلمة سر مؤقتة"}
          </button>
        </div>
      </div>

      <DialogActions>
        <button type="button" className="btn ghost" onClick={onClose}>
          تم
        </button>
        {canApprove && !locked && !isSelf ? (
          <button
            type="button"
            className="btn danger"
            style={{ marginRight: "auto" }}
            onClick={onArchive}
          >
            مسح الحساب
          </button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function ArchiveDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    if (reason.trim().length < MIN_REASON) {
      setError("اكتب سبب المسح — بيتحفظ في السجل");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await archiveUser(user.id, reason.trim());
      onDone(user.full_name_ar);
    } catch (err) {
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="مسح حساب" onClose={onClose} busy={busy}>
      <ApplicantSummary user={user} />

      <Field label="سبب المسح (إجباري)">
        <textarea
          value={reason}
          disabled={busy}
          onChange={(e) => setReason(e.target.value)}
          placeholder="مثال: بريد مكرر — سجّلت ببريد تاني"
          style={{ ...selectStyle, height: 92, padding: 12, resize: "vertical", lineHeight: 1.6 }}
        />
      </Field>

      <div className="t-meta" style={{ lineHeight: 1.7 }}>
        الحساب هيختفي من القوائم وما يقدرش يدخل تاني. البريد بيتحرّر، فصاحبه يقدر
        يسجّل من الأول لو احتاج. والسجل نفسه بيفضل محفوظ للمراجعة.
      </div>

      {error ? <DialogError message={error} /> : null}

      <DialogActions>
        <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
          إلغاء
        </button>
        <button type="button" className="btn danger" disabled={busy} onClick={() => void submit()}>
          {busy ? "بيتمسح…" : "مسح الحساب"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

function CreateUserDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState<AdminRole | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    if (name.trim().length < 2) {
      setError("اكتب اسم المستخدم بالعربي");
      return;
    }
    if (!email.trim()) {
      setError("اكتب البريد اللي هتوصله الدعوة");
      return;
    }
    if (!department) {
      setError("اختار القسم");
      return;
    }
    if (!role) {
      setError("اختار الدور");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createUser({
        full_name_ar: name.trim(),
        email: email.trim(),
        department,
        role,
      });
      onDone(name.trim());
    } catch (err) {
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="إضافة مستخدم" onClose={onClose} busy={busy}>
      <Field label="الاسم">
        <input
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          placeholder="الاسم زي ما هيظهر للفريق"
          style={selectStyle}
        />
      </Field>

      <Field label="البريد">
        <input
          type="email"
          dir="ltr"
          value={email}
          disabled={busy}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          style={{ ...selectStyle, textAlign: "left" }}
        />
      </Field>

      <Field label="القسم">
        <select
          value={department}
          disabled={busy}
          onChange={(e) => setDepartment(e.target.value)}
          style={selectStyle}
        >
          <option value="">اختار القسم</option>
          {DEPARTMENTS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="الدور">
        <select
          value={role}
          disabled={busy}
          onChange={(e) => setRole(e.target.value as AdminRole | "")}
          style={selectStyle}
        >
          <option value="">اختار الدور</option>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </Field>

      <div className="t-meta" style={{ lineHeight: 1.7 }}>
        الحساب بيتعمل مستني تفعيل بلا كلمة سر — التفعيل بدعوة تتبعت من الجدول.
      </div>

      {error ? <DialogError message={error} /> : null}

      <DialogActions>
        <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
          إلغاء
        </button>
        <button type="button" className="btn primary" disabled={busy} onClick={() => void submit()}>
          {busy ? "بيتعمل…" : "إضافة الحساب"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

/* ══════════════════════════════════════════════════════════════════
   قطع مشتركة
   ══════════════════════════════════════════════════════════════════ */

const selectStyle: CSSProperties = {
  width: "100%",
  height: 40,
  borderRadius: "var(--r-control)",
  border: "1px solid var(--line)",
  background: "var(--surface)",
  color: "var(--ink)",
  font: "inherit",
  padding: "0 12px",
};

function Dialog({
  title,
  children,
  onClose,
  busy,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  busy: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // ⚠️ الإغلاق مقفول وإحنا بنبعت — عشان ما يقفلش على فعل نصّه ماشي
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  return (
    <div
      role="presentation"
      onClick={() => {
        if (!busy) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(16, 24, 40, .45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        zIndex: 60,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: 460,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: "20px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          boxShadow: "var(--sh-lift)",
        }}
      >
        <div className="sec-h">
          <span className="sec-title">{title}</span>
        </div>
        {children}
      </div>
    </div>
  );
}

function DialogActions({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>{children}</div>
  );
}

function DialogError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        background: "var(--danger-bg)",
        color: "var(--danger)",
        borderRadius: "var(--r-control)",
        padding: "10px 12px",
        fontSize: "var(--fs-sm)",
        fontWeight: 500,
        lineHeight: 1.6,
      }}
    >
      <Icon name="circle-alert" size={16} stroke="#B42318" />
      <span>{message}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="t-meta">{label}</span>
      {children}
    </label>
  );
}

/** ملخص صاحب الطلب — الاسم والبريد واللي قاله عن نفسه */
function ApplicantSummary({ user }: { user: AdminUser }) {
  return (
    <div
      style={{
        background: "var(--blue-50)",
        borderRadius: "var(--r-control)",
        padding: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div className="t-body">{user.full_name_ar}</div>
      {user.email ? (
        <div className="t-desc">
          <Ltr>{user.email}</Ltr>
        </div>
      ) : null}
      <div className="t-meta">
        قال إنه في:{" "}
        {user.claimed_department ? departmentLabel(user.claimed_department) : "ما كتبش قسم"}
      </div>
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initial = name.trim().slice(0, 1) || "؟";
  return (
    <span
      className="avatar"
      aria-hidden="true"
      style={{ width: 28, height: 28, fontSize: 11.5 }}
    >
      {initial}
    </span>
  );
}

function FlashLine({ flash }: { flash: Flash }) {
  const bad = flash.tone === "bad";
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        borderRadius: "var(--r-control)",
        padding: "10px 14px",
        background: bad ? "var(--danger-bg)" : "var(--ok-bg)",
        color: bad ? "var(--danger)" : "var(--ok)",
        fontSize: "var(--fs-sm)",
        fontWeight: 500,
        lineHeight: 1.6,
      }}
    >
      <Icon
        name={bad ? "circle-alert" : "check"}
        size={16}
        stroke={bad ? "#B42318" : "#067647"}
      />
      <span>{flash.node}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   الأدوار الأربعة — «الدور بيحدّد الشاشات والأزرار اللي بتظهر للمستخدم»
   ══════════════════════════════════════════════════════════════════ */

const ROLE_MATRIX: readonly { title: string; can: string[]; cannot: string[] }[] = [
  { title: "عضو", can: ["مهامه هو بس", "سجلات أسره"], cannot: ["يشوف أقسام تانية", "يعتمد قرارات"] },
  {
    title: "منسق قسم",
    can: ["طابور القسم وإعادة التوزيع", "تقرير القسم"],
    cannot: ["يعتمد قرارات المدير"],
  },
  { title: "مدير", can: ["كل الأقسام", "اعتماد القرارات والأتمتة"], cannot: ["يشوف كلمات السر"] },
  {
    title: "مراقب",
    can: ["يشوف الأقسام المحددة له"],
    cannot: ["أي تعديل", "أي تسليم أو اعتماد"],
  },
];

function RoleMatrix() {
  return (
    <div
      className="card"
      style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div className="sec-h" style={{ flexWrap: "wrap" }}>
        <span className="sec-title">الأدوار الأربعة</span>
        <span className="sec-note">الدور بيحدّد الشاشات والأزرار اللي بتظهر للمستخدم</span>
      </div>
      <div
        style={{
          display: "grid",
          // بيتقسّم لوحده على ١٢٨٠ بدل ما الأعمدة تتزنق
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 20,
        }}
      >
        {ROLE_MATRIX.map((role) => (
          <div
            key={role.title}
            style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}
          >
            <div className="sec-title">{role.title}</div>
            <div className="t-meta">يقدر</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 44 }}>
              {role.can.map((line) => (
                <div key={line} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <Icon name="check" size={15} stroke="#067647" strokeWidth={1.9} />
                  <span className="t-desc clip" style={{ color: "var(--ink-2)" }}>
                    {line}
                  </span>
                </div>
              ))}
            </div>
            <div className="t-meta" style={{ marginTop: 4 }}>
              ما يقدرش
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 44 }}>
              {role.cannot.map((line) => (
                <div key={line} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <Icon name="lock" size={15} stroke="#98A2B3" />
                  <span className="t-desc clip">{line}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
